import { danger, success } from 'redux-store/reducers/common-reducer';
import { hideLoading, showLoading } from 'redux-store/reducers/loading-reducer';
import { hideConfirmModal, showConfirmModal } from 'redux-store/reducers/modal-reducer';
import { fetchCurrentUserOrganizations } from 'modules/organizations/reducers/organizations';

import { API, destroy, get, post, put } from 'utils/api';
import { snakeCaseKeys } from 'utils/keys';
import { __t, applyTranslations } from 'utils/language';
import { logError } from 'utils/log';
import { createReducer } from 'utils/redux';
import { Querystring } from 'utils/url';

import {
  AgencyInfluencersProfileOverview,
  Image,
  InfluencerProfileForm,
  InfluencersAgency,
  PaginationLinks,
  PaginationMeta,
  ThunkAction
} from 'types';
import { FacebookButtonState } from 'components/external-integration/facebook/button/types';

applyTranslations(require('./influencers-agencies-reducer.i18n.json'));
applyTranslations(require('locales/common/collabary-common.i18n.json'));

import type {
  InfluencerAgencyProfileQuery
} from 'modules/influencers-agency/components/influencers-filters/InfluencersFilter';

const PROFILE_LOADED = 'influencersAgencyProfile/LOADED';
const INFLUENCER_PROFILES_LOADED = 'influencerProfiles/LOADED';
const RESET_INFLUENCER_PROFILE = 'influencerProfiles/RESET_INFLUENCER_PROFILE';
const CREATION_NEW_INFLUENCER_PROFILES = 'influencerProfiles/CREATION_NEW_INFLUENCER_PROFILES';
const RESET_ALL_INFLUENCER_PROFILE = 'influencerProfiles/RESET_ALL_INFLUENCER_PROFILE';

export type InfluencerProfileFormWithFacebookState = InfluencerProfileForm & { facebookButtonState?: FacebookButtonState };

type InfluencersAgenciesReducerState = {
  profile: InfluencersAgency,
  influencerProfiles: {
    data: Array<AgencyInfluencersProfileOverview>,
    meta: PaginationMeta | null,
    links: PaginationLinks | null
  },
  newInfluencerProfiles: {
    [id: string]: InfluencerProfileFormWithFacebookState
  }
}

const initialState = {
  profile: {} as InfluencersAgency,
  newInfluencerProfiles: {},
  influencerProfiles: {
    data: [],
    meta: null,
    links: null
  }
};

const influencersAgencyProfileLoaded = (data: InfluencersAgency) => ({
  type: PROFILE_LOADED,
  payload: {
    data
  }
});

const influencerProfilesLoaded = (data: {
  data: Array<AgencyInfluencersProfileOverview>,
  meta: PaginationMeta | null,
  links: PaginationLinks | null
}) => ({
  type: INFLUENCER_PROFILES_LOADED,
  payload: {
    data
  }
});

export const showBusinessAndBankDetailsConfirmModal = (onConfirm: () => void) => showConfirmModal({
  onConfirm,
  title: __t('influencers_agencies_reducer.business__and_bank_details_confirm_modal.title'),
  message: __t('influencers_agencies_reducer.business__and_bank_details_confirm_modal.message'),
  confirmButtonLabel: __t('collabary_common.action_button.close')
});

export const hideBusinessAndBankDetailsConfirmModal = () => hideConfirmModal();

export const saveInfluencerProfile = (details: InfluencerProfileFormWithFacebookState, id) => ({
  type: CREATION_NEW_INFLUENCER_PROFILES,
  payload: {
    details,
    id
  }
});

export const resetInfluencerProfile = (id) => ({
  type: RESET_INFLUENCER_PROFILE,
  payload: {
    id
  }
});

export const resetAllInfluencerProfile = () => ({
  type: RESET_ALL_INFLUENCER_PROFILE
});

export function uploadImage(type, image) {
  const url = API.END_POINTS.API.IMAGES;

  return (dispatch) => {
    if (!image.file) {
      return Promise.resolve(image);
    }

    dispatch(showLoading(url));

    const data = new FormData();
    data.append('file', image.file);

    return post(url, { data })
      .then((response) => ({
        area: image.area,
        ...response.data
      } as Image))
      .catch((error) => {
        const errorMessage = __t(type === 'cover'
          ? 'influencers_agencies_reducer.cover_image_upload_error'
          : 'influencers_agencies_reducer.profile_image_upload_error'
        );

        dispatch(showConfirmModal({
          message: errorMessage,
          confirmButtonLabel: __t('collabary_common.action_button.ok'),
          onConfirm: () => dispatch(hideConfirmModal())
        }));
        logError(error);
      })
      .finally(() => {
        dispatch(hideLoading(url));
      });
  };
}

export function fetchInfluencersAgency(agencyId: number):ThunkAction {
  const url = API.END_POINTS.INFLUENCERS_AGENCIES.influencersAgency(agencyId);

  return (dispatch) => {
    dispatch(showLoading(url));

    return get(url)
      .then((response) => {
        dispatch(influencersAgencyProfileLoaded(response.data));
      })
      .catch((error) => {
        logError(error);
        dispatch(danger(error.message || __t('influencers_agencies_reducer.fetch_influencers_agency_error')));
      })
      .finally(() => {
        dispatch(hideLoading(url));
      });
  };
}

export function createInfluencerProfiles(agencyId, profiles: Array<InfluencerProfileFormWithFacebookState>): ThunkAction {
  const url = API.END_POINTS.INFLUENCERS_AGENCIES.createInfluencerProfilesForAgency(agencyId);

  return (dispatch) => {
    dispatch(showLoading(url));

    return post(url, {
      data: {
        profiles: profiles.map(profile => {
          const facebookCode = profile.facebookButtonState?.authResponse?.accessToken;
          const updatedProfile = {
            ...profile,
            facebookUser: facebookCode ? {
              facebookCode
            } : null
          };

          delete updatedProfile.facebookButtonState;

          return updatedProfile;
        })
      }
    }, {
      suppressGlobalNotification: true
    })
      .then(() => {
        dispatch(success(__t('influencers_agencies_reducer.create_influencer_profiles_success')));

        dispatch(fetchCurrentUserOrganizations());

        return true;
      })
      .catch((error) => {
        logError(error);

        dispatch(danger(__t('influencers_agencies_reducer.create_influencer_profiles_error')));

        return false;
      })
      .finally(() => {
        dispatch(hideLoading(url));
      });
  };
}

export function updateInfluencersAgency(agency: InfluencersAgency):ThunkAction {
  const url = API.END_POINTS.INFLUENCERS_AGENCIES.influencersAgency(agency.id);

  return (dispatch) => {
    dispatch(showLoading(url));

    return put(url, { data: agency })
      .then((response) => {
        dispatch(influencersAgencyProfileLoaded(response.data));
        dispatch(success(__t('influencers_agencies_reducer.update_influencers_agency_success'), { keepBetweenPages: true }));
      })
      .catch((error) => {
        logError(error);
        if (error.response?.status === 409 && error.response?.data?.detail) {
          dispatch(danger(error.response?.data?.detail));
        } else {
          dispatch(danger(__t('influencers_agencies_reducer.update_influencers_agency_error')));
        }
      })
      .finally(() => {
        dispatch(hideLoading(url));
      });
  };
}

export function getAgencyInfluencers(query: InfluencerAgencyProfileQuery, agencyId: number): ThunkAction {
  const searchParams = Querystring.objectToString(snakeCaseKeys({ ...query }));

  const url = API.END_POINTS.INFLUENCERS_AGENCIES.influencersAgencyProfiles(agencyId, searchParams);

  return (dispatch) => {
    dispatch(showLoading(url));

    return get(url, {}, {
      suppressGlobalNotification: true
    })
      .then((response) => {
        dispatch(influencerProfilesLoaded(response.data));
      })
      .catch((error) => {
        logError(error);

        dispatch(influencerProfilesLoaded({ data: [], meta: null, links: null }));
        dispatch(danger(__t('influencers_agencies_reducer.get_influencers_profile_error')));
      })
      .finally(() => {
        dispatch(hideLoading(url));
      });
  };
}

export function deleteInfluencerProfile(
  agencyId: number,
  influencerProfileId: number
): ThunkAction {
  const url = API.END_POINTS.INFLUENCERS_AGENCIES.deleteInfluecnerProfile(
    agencyId,
    influencerProfileId
  );

  return dispatch => {
    dispatch(showLoading(url));

    return destroy(url)
      .catch((error) => {
        logError(error);
        dispatch(danger(
          error.message || __t('influencers_agencies_reducer.delete_influencer_profiles_error'))
        );
      })
      .finally(() => dispatch(hideLoading(url)));
  };
}

export default createReducer<InfluencersAgenciesReducerState, any>('influencersAgency', initialState, {
  [PROFILE_LOADED]: (state, { data }) => ({
    ...state,
    profile: data
  }),
  [INFLUENCER_PROFILES_LOADED]: (state, { data }) => ({
    ...state,
    influencerProfiles: {
      ...data
    }
  }),
  [CREATION_NEW_INFLUENCER_PROFILES]: (state, { details, id }) => ({
    ...state,
    newInfluencerProfiles: {
      ...state.newInfluencerProfiles,
      [id]: details
    }
  }),
  [RESET_INFLUENCER_PROFILE]: (state, { id }) => {
    const newInfluencerProfiles = { ...state.newInfluencerProfiles };

    delete newInfluencerProfiles[id];

    return ({
      ...state,
      newInfluencerProfiles
    });
  },
  [RESET_ALL_INFLUENCER_PROFILE]: (state) => ({
    ...state,
    newInfluencerProfiles: {}
  })
});
